import React, { FC, Suspense, useEffect, useState } from "react";
import { inject, observer, Provider } from "mobx-react";
import { Routes, Route, Navigate, NavigateFunction } from "react-router-dom";
import { NoPermissionDialog, withRouter } from "@mindmemobile/mm-shared-global";
import {
  CheckOutStore,
  ItemsStore,
  PurchaseStore,
  RolesAndPermissionStore,
} from "../shared/store";
import {
  MerchantAccountRoutes,
  MerchantAccountStore,
} from "./merchantAccounts";
import { DashboardMain, DashboardStore } from "./dashboard";
import { MerchantAccount } from "../shared/models";
import { AuthService, MerchantAccountService } from "../shared/services";
import { ApplicationStatus } from "../shared/enums";
import { ServiceRoutes } from "./services";
import RootStore from "./RootStore";
import HeaderBarMain from "./HeaderBarMain";
import { ProductRoutes } from "./products";
import { PurchaseRoutes } from "./purchase";
import {
  CheckoutDonation,
  CheckoutPreview,
  CheckoutPreviewForDetailPage,
  CheckOutRoutes,
  CheckoutsMain,
} from "./checkouts";
import { MembershipRoutes } from "./membership";
import { DonationRoutes } from "./donation";
import { DashboardGrid } from "../shared/styles";
import { DonationGridsMain, ItemRoutes } from "./items";
import { ContactRoutes } from "./contact";
import { OrderRoutes } from "./orders";
import { SubscriptionRoutes } from "./subscription";
import { PaymentRoutes } from "./payment";
import { InvoiceRoutes } from "./invoices";
import { PurchaseLinkRoutes } from "./purchase-link";
import { PaymentSettingsRoutes } from "./paymentSettings";  
import DisputesRoutes from "./disputes/DisputesRoutes";  
import FundingRoutes from "./funding/FundingRoutes";

interface IProps {
  location: Location;
  navigate: NavigateFunction;
  itemStore: ItemsStore;
  rolesAndPermissionStore: RolesAndPermissionStore;
}

const merchantAccountStore = new MerchantAccountStore();
const checkoutStore = new CheckOutStore();
const purchaseStore = new PurchaseStore();
const dashboardStore = new DashboardStore();

const AppRoutes: FC<IProps> = ({
  location,
  itemStore,
  rolesAndPermissionStore,
}) => {
  const [merchantAccounts, setMerchantAccounts] = useState<MerchantAccount[]>(
    []
  );
  const [isMerchantAccountLoading, setMerchantAccountLoading] = useState(true);
  const [selectedMerchantAccountId, setMerchantAccountId] =
    useState<string>("");
  useEffect(() => {
    if (location.pathname.includes("app/activity")) {
      getMerchantAccounts();
    } else {
      AuthService.setLastRoute(location.pathname);
    }
  }, [location]);

  function hideHeader(): boolean {
    //if (location.pathname.includes("app/items")) return false;
    let isHide =
      location.pathname.includes("app/service") ||
      location.pathname.includes("app/product") ||
      location.pathname.includes("app/membership") ||
      location.pathname.includes("/app/checkout/") ||
      location.pathname.includes("/app/donation/") ||
      location.pathname.includes("purchase/checkout/") ||
      location.pathname.includes("purchase/purchase-link/");
    if (isHide) {
      //send message to parent window to hide header
      postMessageToParentWindow("hideHeader");
    } else {
      //send message to parent window to hide header
      postMessageToParentWindow("showHeader");
    }
    isHide = isHide || !AuthService.fromPaymentSystem;
    return isHide;
  }

  function postMessageToParentWindow(type: string) {
    if (!AuthService.fromPaymentSystem && window.parent) {
      addScriptToDocument();
      const { innerWidth: width, innerHeight: height } = window;
      const message = {
        type: type,
        width,
        height,
        showIframe: true,
      };
      window.parent.postMessage(JSON.stringify(message), "*");
    }
  }

  function addScriptToDocument() {
    const style = document.createElement("style");
    const contactDetailBackgroundColor = AuthService.isMindMeContactRoute
      ? "#ededed"
      : "";
    // Add custom styles
    style.textContent = `
     height: 100%;
background-color: #f4f7fb;
html {
  height: 100%;
  background-color: #f4f7fb;
}
body {
  height: 100%;
  background-color: #f4f7fb;
}
div#root {
  height: 100%;
  background-color: #f4f7fb;
}
iframe{
  background-color: #f4f7fb;
}
  .contact-routes-only {
    background-color: ${contactDetailBackgroundColor};
  }
    /* Add more custom styles here */
    `;
    // Append the style element to the head of the iframe's document
    document.head.appendChild(style);
  }

  function isPurchaseLinkPath(): boolean {
    return location.pathname.includes("purchase-link");
  }

  const getMerchantAccounts = () => {
    if (!AuthService.hasAccount) {
      setMerchantAccountLoading(false);
      return;
    }
    setMerchantAccountLoading(true);

    MerchantAccountService.getMerchantAccounts(
      AuthService.authData.accountId,
      1,
      10000,
      ""
    ).subscribe((response) => {
      if (response.result) {
        const allMerchantAccounts =
          response.result?.map((m) => MerchantAccount.deserializeAPIModel(m)) ||
          [];
        const activeMerchantAccounts = (
          response.result?.map((m) => MerchantAccount.deserializeAPIModel(m)) ||
          []
        ).filter((f) => f.applicationStatus === ApplicationStatus.Active);

        setMerchantAccounts(allMerchantAccounts);
        if (activeMerchantAccounts.length > 0 && !selectedMerchantAccountId) {
          setMerchantAccountId(activeMerchantAccounts[0].merchantAccountId);
        }
      }
      setMerchantAccountLoading(false);
    });
    merchantAccountStore.fetchMerchantAccounts();
  };

  if (location.pathname.includes("app/activity")) {
    if (!isMerchantAccountLoading) {
      if (
        merchantAccounts.length > 0 &&
        !merchantAccounts.some(
          (x) => x.applicationStatus === ApplicationStatus.Active
        ) &&
        !AuthService.fromPaymentSystem &&
        AuthService.hasAccount &&
        !AuthService.lastRoute //NOT having for last route means first route
      ) {
        return <Navigate to="/app/merchant-accounts" />;
      }
    } else {
      return null;
    }
  }
  // if (
  //   !isMerchantAccountLoading &&
  //   !AuthService.fromPaymentSystem &&
  //   !AuthService.lastRoute //NOT having for last route means first route
  // ) {
  //   const pendingMerchant = merchantAccounts.find(
  //     (m) => m.applicationStatus === ApplicationStatus.Pending
  //   );
  //   if (
  //     !location.pathname.includes("/app/merchant-accounts/pending") &&
  //     merchantAccounts.length === 1 &&
  //     pendingMerchant &&
  //     AuthService.hasAccount
  //   ) {
  //     return (
  //       <Navigate to={`/app/merchant-accounts/success/${pendingMerchant.id}`} />
  //     );
  //   }
  // }
  return (
    <Suspense>
      <Provider {...RootStore}>
        {!hideHeader() && AuthService.fromPaymentSystem && <HeaderBarMain />}
        <NoPermissionDialog
          showPopup={rolesAndPermissionStore.showPopup}
          title={rolesAndPermissionStore.title}
          message={rolesAndPermissionStore.message}
          togglePermissionPopup={(value) =>
            rolesAndPermissionStore.togglePermissionPopup(value)
          }
        />
        <Routes>
          <Route
            path={`/service/*`}
            element={
              <ServiceRoutes
                rolesAndPermissionStore={rolesAndPermissionStore}
                merchantAccountStore={merchantAccountStore}
              />
            }
          />
          <Route
            path={`/product/*`}
            element={
              <ProductRoutes
                rolesAndPermissionStore={rolesAndPermissionStore}
                merchantAccountStore={merchantAccountStore}
              />
            }
          />
          <Route
            path={`/purchase/:type/*`}
            element={
              <PurchaseRoutes
                purchaseStore={purchaseStore}
                isPurchaseLink={isPurchaseLinkPath()}
                rolesAndPermissionStore={rolesAndPermissionStore}
                merchantAccountStore={merchantAccountStore}
              />
            }
          />
          <Route
            path={`/checkout/*`}
            element={
              <CheckOutRoutes
                checkoutStore={checkoutStore}
                rolesAndPermissionStore={rolesAndPermissionStore}
                merchantAccountStore={merchantAccountStore}
              />
            }
          />
          <Route
            path={`/checkout/donation/:id`}
            element={<CheckoutDonation checkoutStore={checkoutStore} />}
          />
          <Route path={`/checkout/preview`} element={<CheckoutPreview />} />
          <Route
            path={`/checkout/preview/detail`}
            element={<CheckoutPreviewForDetailPage />}
          />
          <Route
            path={`/checkouts/*`}
            element={
              <CheckoutsMain
                itemStore={itemStore}
                rolesAndPermissionStore={rolesAndPermissionStore}
                merchantAccountStore={merchantAccountStore}
              />
            }
          />
          <Route
            path={`/membership/*`}
            element={
              <MembershipRoutes
                rolesAndPermissionStore={rolesAndPermissionStore}
                merchantAccountStore={merchantAccountStore}
              />
            }
          />
          <Route
            path={`/donation/*`}
            element={
              <DonationRoutes
                rolesAndPermissionStore={rolesAndPermissionStore}
                merchantAccountStore={merchantAccountStore}
              />
            }
          />
          <Route
            path={`/donations/*`}
            element={
              <DashboardGrid>
                <DonationGridsMain
                  itemStore={itemStore}
                  rolesAndPermissionStore={rolesAndPermissionStore}
                  merchantAccountStore={merchantAccountStore}
                />
              </DashboardGrid>
            }
          />
          <Route
            path={`/items/*`}
            element={
              <ItemRoutes
                itemStore={itemStore}
                rolesAndPermissionStore={rolesAndPermissionStore}
                merchantAccountStore={merchantAccountStore}
              />
            }
          />
          <Route
            path={`/contacts/*`}
            element={
              <DashboardGrid className="contact-routes-only">
                <ContactRoutes
                  itemStore={itemStore}
                  contactStore={itemStore.contactStore}
                  rolesAndPermissionStore={rolesAndPermissionStore}
                  merchantAccountStore={merchantAccountStore}
                />
              </DashboardGrid>
            }
          />
          <Route
            path={`/activity/*`}
            element={
              <DashboardGrid>
                <DashboardMain
                  itemStore={itemStore}
                  merchantAccountId={selectedMerchantAccountId}
                  merchantAccounts={merchantAccounts}
                  dashboardStore={dashboardStore}
                  onChangeMerchantAccount={(merchantAccountId: string) => {
                    setMerchantAccountId(merchantAccountId);
                  }}
                  isMerchantAccountLoading={isMerchantAccountLoading}
                  rolesAndPermissionStore={rolesAndPermissionStore}
                  merchantAccountStore={merchantAccountStore}
                />
              </DashboardGrid>
            }
          />

          <Route
            path={`/orders/*`}
            element={
              <DashboardGrid>
                <OrderRoutes
                  itemStore={itemStore}
                  orderStore={itemStore.orderStore}
                  rolesAndPermissionStore={rolesAndPermissionStore}
                  merchantAccountStore={merchantAccountStore}
                />
              </DashboardGrid>
            }
          />
          <Route
            path={`/funding/*`}
            element={
              <DashboardGrid>
                <FundingRoutes
                  itemStore={itemStore}
                  fundingStore={itemStore.fundingStore}
                  rolesAndPermissionStore={rolesAndPermissionStore}
                  merchantAccountStore={merchantAccountStore}
                />
              </DashboardGrid>
            }
          />
          <Route
            path={`/disputes/*`}
            element={
              <DashboardGrid>
                <DisputesRoutes
                  itemStore={itemStore}
                  rolesAndPermissionStore={rolesAndPermissionStore}
                  merchantAccountStore={merchantAccountStore}
                />
              </DashboardGrid>
            }
          />
          <Route
            path={`/merchant-accounts/*`}
            element={
              <MerchantAccountRoutes
                itemStore={itemStore}
                merchantAccountStore={merchantAccountStore}
                rolesAndPermissionStore={rolesAndPermissionStore}
                getMerchantAccounts={() => getMerchantAccounts()}
              />
            }
          />
          <Route
            path={`/subscriptions/*`}
            element={
              <SubscriptionRoutes
                itemStore={itemStore}
                subscriptionStore={itemStore.subscriptionStore}
                rolesAndPermissionStore={rolesAndPermissionStore}
                merchantAccountStore={merchantAccountStore}
              />
            }
          />
          <Route
            path={`/payments/*`}
            element={
              <DashboardGrid>
                <PaymentRoutes
                  itemStore={itemStore}
                  paymentStore={itemStore.paymentStore}
                  rolesAndPermissionStore={rolesAndPermissionStore}
                  merchantAccountStore={merchantAccountStore}
                />
              </DashboardGrid>
            }
          />
          <Route
            path={`/invoices/*`}
            element={
              <DashboardGrid>
                <InvoiceRoutes
                  itemStore={itemStore}
                  invoiceStore={itemStore.invoiceStore}
                  rolesAndPermissionStore={rolesAndPermissionStore}
                  merchantAccountStore={merchantAccountStore}
                />
              </DashboardGrid>
            }
          />
          <Route
            path={`/purchase-link/*`}
            element={
              <DashboardGrid>
                <PurchaseLinkRoutes
                  itemStore={itemStore}
                  rolesAndPermissionStore={rolesAndPermissionStore}
                  merchantAccountStore={merchantAccountStore}
                />
              </DashboardGrid>
            }
          />
          <Route
            path="/payment-settings/*"
            element={
              <DashboardGrid>
                <PaymentSettingsRoutes
                  itemStore={itemStore}
                  rolesAndPermissionStore={rolesAndPermissionStore}
                  merchantAccountStore={merchantAccountStore}
                ></PaymentSettingsRoutes>
              </DashboardGrid>
            }
          />
          <Route path="*" element={<Navigate to="/app/activity" />} />
        </Routes>
      </Provider>
    </Suspense>
  );
};

export default inject(
  "itemStore",
  "rolesAndPermissionStore"
)(withRouter(observer(AppRoutes)));
